import { useEffect, useMemo, useRef, useState } from "react";
import { fetchMemberDashboard } from "../api/fetchMemberDashboard/fetchMemberDashboardApi";
import { MemberDashboard } from "../api/fetchMemberDashboard/types";
import { KPI_CONFIG, PNL_SECTION } from "../config/kpiConfig";
import { DashboardKPI } from "../types";
import { toDashboardKPI } from "../utils/toDashboardKPI";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `2026-07` → `Jul 2026`. */
const longMonth = (yearMonth: string): string =>
  `${MONTH_LABELS[Number(yearMonth.slice(5, 7)) - 1] ?? yearMonth} ${yearMonth.slice(0, 4)}`;

/**
 * Page state for the member KPI dashboard.
 *
 * The KPIs the API returns are paired with their entry in `config/kpiConfig.ts`;
 * anything without one is skipped, so a newly-calculated KPI cannot reach the
 * page as a card with blank prose.
 */
export const useMemberDashboardPage = () => {
  const [dashboard, setDashboard] = useState<MemberDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchMemberDashboard(controller.signal)
      .then((response) => setDashboard(response.data))
      .finally(() => {
        // An aborted request has been superseded; letting it clear the flag
        // would flash the empty state under the request that replaced it.
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  const kpis: DashboardKPI[] = useMemo(
    () =>
      (dashboard?.kpis ?? [])
        .filter((kpi) => KPI_CONFIG[kpi.code])
        .map((kpi) => toDashboardKPI(kpi, KPI_CONFIG[kpi.code])),
    [dashboard],
  );

  const periodLabel = dashboard
    ? `${longMonth(dashboard.periodStart)} – ${longMonth(dashboard.periodEnd)}`
    : null;

  return {
    section: PNL_SECTION,
    kpis,
    periodLabel,
    isLoading,
  };
};
